import React, { useCallback, useEffect, useState } from 'react';
import {
    Check,
    CheckCircle2,
    ClipboardCheck,
    Clock3,
    ExternalLink,
    Gift,
    Inbox,
    RefreshCw,
    X,
} from 'lucide-react';

import { API_BASE_URL } from '../../config';

const STATUS_OPTIONS = [
    { value: 'pending_fulfillment', label: 'Ожидают выдачи', icon: Clock3 },
    { value: 'fulfilled', label: 'Выданы', icon: CheckCircle2 },
];

const REWARD_LABELS = {
    bonus: 'Бонусы',
    telegram_gift: 'Telegram-подарок',
    stars: 'Telegram Stars',
    premium: 'Telegram Premium',
    digital_good: 'Цифровой товар',
    manual: 'Другой приз',
    none: 'Без приза',
};

const AUTOMATIC_STATUS_LABELS = {
    queued: 'В очереди',
    processing: 'Отправляется',
    waiting_configuration: 'Нужна настройка',
    dry_run: 'Проверено без отправки',
    retry_scheduled: 'Повтор запланирован',
    manual_review: 'Нужна проверка',
    delivered: 'Отправлен автоматически',
    cancelled: 'Отменён',
};

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const userPrimary = (user) => {
    if (user?.username) return `@${String(user.username).replace(/^@/, '')}`;
    return user?.email || user?.fio || `Пользователь #${user?.id || '—'}`;
};

const RouletteFulfillmentPanel = ({ onShowToast }) => {
    const [status, setStatus] = useState('pending_fulfillment');
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeId, setActiveId] = useState(null);
    const [form, setForm] = useState({ external_reference: '', note: '' });
    const [savingId, setSavingId] = useState(null);
    const [retryingId, setRetryingId] = useState(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await fetch(
                `${API_BASE_URL}/api/settings/roulette/fulfillment?status=${encodeURIComponent(status)}&page_size=100`,
                { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
            );
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.detail || 'Не удалось загрузить очередь');
            setData({ items: body.items || [], total: body.total || 0 });
        } catch (loadError) {
            setError(loadError.message || 'Не удалось загрузить очередь');
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => {
        setActiveId(null);
        load();
    }, [load]);

    const beginFulfillment = (spin) => {
        setActiveId(spin.id);
        setForm({ external_reference: '', note: '' });
    };

    const fulfill = async (spinId) => {
        try {
            setSavingId(spinId);
            setError('');
            const response = await fetch(`${API_BASE_URL}/api/settings/roulette/fulfillment/${spinId}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    external_reference: form.external_reference.trim() || null,
                    note: form.note.trim() || null,
                }),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.detail || 'Не удалось подтвердить выдачу');
            setActiveId(null);
            if (onShowToast) onShowToast(body.changed ? 'Выдача приза подтверждена' : 'Приз уже был отмечен выданным');
            await load();
        } catch (saveError) {
            setError(saveError.message || 'Не удалось подтвердить выдачу');
        } finally {
            setSavingId(null);
        }
    };

    const retryAutomatic = async (fulfillmentId) => {
        try {
            setRetryingId(fulfillmentId);
            setError('');
            const response = await fetch(
                `${API_BASE_URL}/api/settings/roulette/gift-delivery/fulfillments/${fulfillmentId}/retry`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ acknowledge_duplicate_risk: false }),
                },
            );
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(typeof body.detail === 'string' ? body.detail : body.detail?.message || 'Не удалось повторить выдачу');
            onShowToast?.('Автоматическая выдача поставлена в очередь');
            await load();
        } catch (retryError) {
            setError(retryError.message || 'Не удалось повторить выдачу');
        } finally {
            setRetryingId(null);
        }
    };

    return (
        <div className="space-y-5">
            {error && (
                <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {error}
                </div>
            )}

            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                    <div className="flex gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                            <ClipboardCheck size={20} />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-900">Выдача призов</h3>
                            <p className="mt-1 text-sm leading-5 text-slate-500">
                                Автоматические подарки и ручные выигрыши собраны в одной очереди.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-50"
                    >
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Обновить
                    </button>
                </div>

                <div className="flex gap-1 overflow-x-auto border-b border-slate-100 bg-slate-50 px-4 py-2 sm:px-5">
                    {STATUS_OPTIONS.map((option) => {
                        const Icon = option.icon;
                        const selected = status === option.value;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => setStatus(option.value)}
                                className={`inline-flex h-9 shrink-0 items-center gap-2 rounded-lg px-3 text-sm font-semibold transition ${selected ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-800'}`}
                            >
                                <Icon size={16} /> {option.label}
                            </button>
                        );
                    })}
                    <div className="ml-auto flex shrink-0 items-center px-3 text-xs font-semibold text-slate-400">
                        Всего: {data.total}
                    </div>
                </div>

                {loading ? (
                    <div className="py-16 text-center text-sm text-slate-500">Загрузка выигрышей...</div>
                ) : data.items.length === 0 ? (
                    <div className="px-5 py-14 text-center">
                        <Inbox className="mx-auto text-slate-300" size={30} />
                        <h4 className="mt-3 font-semibold text-slate-900">
                            {status === 'pending_fulfillment' ? 'Очередь пуста' : 'Выданных призов пока нет'}
                        </h4>
                        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
                            {status === 'pending_fulfillment'
                                ? 'Новые внешние выигрыши появятся здесь сразу после вращения.'
                                : 'После подтверждения выдачи запись останется в журнале.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[960px] border-collapse text-left">
                            <thead>
                                <tr className="border-b border-slate-100 text-xs uppercase text-slate-500">
                                    <th className="px-5 py-3 font-semibold">Пользователь</th>
                                    <th className="px-4 py-3 font-semibold">Приз</th>
                                    <th className="px-4 py-3 font-semibold">Уровень</th>
                                    <th className="px-4 py-3 font-semibold">Создан</th>
                                    <th className="px-4 py-3 font-semibold">Подтверждение</th>
                                    <th className="px-5 py-3 text-right font-semibold">Действие</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((spin) => (
                                    <React.Fragment key={spin.id}>
                                        <tr className="border-b border-slate-100 align-middle">
                                            <td className="px-5 py-4">
                                                <div className="font-semibold text-slate-900">{userPrimary(spin.user)}</div>
                                                <div className="mt-1 text-xs text-slate-500">
                                                    TG ID: {spin.user?.tg_id || 'не привязан'} · ID: {spin.user?.id}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex items-center gap-2 font-semibold text-slate-900">
                                                    <Gift size={16} className="text-indigo-500" /> {spin.prize_title}
                                                </div>
                                                <div className="mt-1 text-xs text-slate-500">
                                                    {REWARD_LABELS[spin.reward_type] || spin.reward_type}
                                                    {spin.reward_value > 0 ? ` · ${spin.reward_value.toLocaleString('ru-RU')}` : ''}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="text-sm font-medium text-slate-700">{spin.tier_title}</div>
                                                <div className="mt-1 text-xs text-slate-500">Списано {spin.cost_bonus.toLocaleString('ru-RU')}</div>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-slate-600">{formatDate(spin.created_at)}</td>
                                            <td className="px-4 py-4">
                                                {spin.automatic_fulfillment ? (
                                                    <div className="max-w-[280px]">
                                                        <div className={`text-sm font-semibold ${spin.automatic_fulfillment.status === 'delivered' ? 'text-emerald-700' : spin.automatic_fulfillment.status === 'manual_review' ? 'text-amber-700' : 'text-slate-700'}`}>
                                                            {AUTOMATIC_STATUS_LABELS[spin.automatic_fulfillment.status] || spin.automatic_fulfillment.status}
                                                        </div>
                                                        <div className="mt-1 text-xs text-slate-500">
                                                            {spin.automatic_fulfillment.provider === 'telegram'
                                                                ? 'Telegram Bot API'
                                                                : spin.automatic_fulfillment.provider === 'collectible'
                                                                    ? 'Collectible Marketplace'
                                                                    : spin.automatic_fulfillment.provider} · попыток {spin.automatic_fulfillment.attempt_count || 0}
                                                        </div>
                                                        {spin.automatic_fulfillment.last_error_message && (
                                                            <div className="mt-1 text-xs leading-4 text-amber-700">{spin.automatic_fulfillment.last_error_message}</div>
                                                        )}
                                                        {spin.automatic_fulfillment.provider_transaction_id && (
                                                            <div className="mt-1 truncate font-mono text-[11px] text-slate-400" title={spin.automatic_fulfillment.provider_transaction_id}>
                                                                {spin.automatic_fulfillment.provider_transaction_id}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : spin.fulfillment ? (
                                                    <div>
                                                        <div className="text-sm font-medium text-slate-700">
                                                            {spin.fulfillment.external_reference || 'Без номера'}
                                                        </div>
                                                        <div className="mt-1 text-xs text-slate-500">
                                                            {formatDate(spin.fulfillment.fulfilled_at)}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-slate-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-5 py-4 text-right">
                                                {spin.status === 'pending_fulfillment' ? (
                                                    <div className="flex justify-end gap-2">
                                                        {spin.automatic_fulfillment
                                                            && !['queued', 'processing', 'retry_scheduled'].includes(spin.automatic_fulfillment.status)
                                                            && !String(spin.automatic_fulfillment.last_error_code || '').endsWith('UNCERTAIN') && (
                                                            <button
                                                                type="button"
                                                                onClick={() => retryAutomatic(spin.automatic_fulfillment.id)}
                                                                disabled={retryingId === spin.automatic_fulfillment.id}
                                                                className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-50"
                                                            >
                                                                <RefreshCw size={15} className={retryingId === spin.automatic_fulfillment.id ? 'animate-spin' : ''} /> Повторить
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => beginFulfillment(spin)}
                                                            className="inline-flex h-9 items-center gap-2 rounded-lg bg-indigo-600 px-3 text-sm font-semibold text-white hover:bg-indigo-700"
                                                        >
                                                            <Check size={16} /> {spin.automatic_fulfillment ? 'Выдан вручную' : 'Подтвердить'}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700">
                                                        <CheckCircle2 size={16} /> Выдан
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                        {activeId === spin.id && (
                                            <tr className="border-b border-indigo-100 bg-indigo-50/60">
                                                <td colSpan="6" className="px-5 py-4">
                                                    <div className="grid gap-4 lg:grid-cols-[minmax(220px,0.7fr)_minmax(300px,1.3fr)_auto] lg:items-end">
                                                        <label className="block">
                                                            <span className="mb-1.5 block text-xs font-semibold uppercase text-slate-500">Номер операции или заявки</span>
                                                            <div className="relative">
                                                                <ExternalLink className="absolute left-3 top-2.5 text-slate-400" size={17} />
                                                                <input
                                                                    value={form.external_reference}
                                                                    onChange={(event) => setForm((current) => ({ ...current, external_reference: event.target.value }))}
                                                                    maxLength={160}
                                                                    placeholder="Например, ticket-142"
                                                                    className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                                                                />
                                                            </div>
                                                        </label>
                                                        <label className="block">
                                                            <span className="mb-1.5 block text-xs font-semibold uppercase text-slate-500">Комментарий</span>
                                                            <input
                                                                value={form.note}
                                                                onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))}
                                                                maxLength={500}
                                                                placeholder="Как и куда выдан приз"
                                                                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                                                            />
                                                        </label>
                                                        <div className="flex gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => setActiveId(null)}
                                                                className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-slate-800"
                                                                title="Отмена"
                                                            >
                                                                <X size={17} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => fulfill(spin.id)}
                                                                disabled={savingId === spin.id}
                                                                className="inline-flex h-10 items-center gap-2 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                                                            >
                                                                <Check size={17} /> {savingId === spin.id ? 'Сохранение...' : 'Приз выдан'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <p className="mt-3 text-xs leading-5 text-indigo-800">
                                                        Подтверждение меняет только статус выдачи. Повторно бонусы или приз не начисляются.
                                                    </p>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
};

export default RouletteFulfillmentPanel;
